import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { FolderLock, Link2, Search, Ticket, User } from 'lucide-react'
import type { ZoekUitkomst } from '../../gedeeld/types'
import { api } from '../lib/api'
import { navigeer } from '../lib/router'
import { zet } from '../lib/staat'
import { geheimSoort, itemSoort, systeemSoort, ticketNummer, ticketStatus } from '../lib/woorden'
import { kaalAdres } from '../lib/opmaak'
import { Laden } from './ui'

/* ===========================================================================
 *  Zoeken in alles wat je mag zien
 *
 *  Ctrl+K of /. De functie zoekt ALS jou: een medewerker vindt zijn eigen
 *  tickets en spullen en de codes die met hem gedeeld zijn, IT vindt alles.
 *  Een code zelf staat nooit in de uitkomst -- alleen de naam.
 * =========================================================================== */

interface Regel { sleutel: string; groep: string; titel: string; sub?: string; icoon: ReactNode; ga: () => void }

export default function Zoeken() {
  const [q, setQ] = useState('')
  const [uit, setUit] = useState<ZoekUitkomst | null>(null)
  const [bezig, setBezig] = useState(false)
  const [gekozen, setGekozen] = useState(0)
  const veld = useRef<HTMLInputElement>(null)
  const sluit = () => zet({ zoekOpen: false })

  useEffect(() => { veld.current?.focus() }, [])

  useEffect(() => {
    const t = q.trim()
    if (t.length < 2) { setUit(null); return }
    setBezig(true)
    const klok = setTimeout(() => {
      api.zoeken(t).then(setUit).catch(() => setUit(null)).finally(() => setBezig(false))
    }, 180)
    return () => clearTimeout(klok)
  }, [q])

  const regels = useMemo<Regel[]>(() => {
    if (!uit) return []
    const r: Regel[] = []
    const ga = (naar: string) => () => { sluit(); navigeer(naar) }
    for (const t of uit.tickets) {
      r.push({ sleutel: 't' + t.id, groep: 'Tickets', titel: t.titel, sub: `${ticketNummer(t.nummer)} · ${ticketStatus(t.status).label}`, icoon: <Ticket size={15} />, ga: ga(`/t/${t.id}`) })
    }
    for (const g of uit.geheimen) {
      const S = geheimSoort(g.soort).icoon
      r.push({ sleutel: 'g' + g.id, groep: 'Kluis', titel: g.titel, sub: g.gebruikersnaam ?? geheimSoort(g.soort).label, icoon: <S size={15} />, ga: ga(`/kluis/${g.id}`) })
    }
    for (const i of uit.items) {
      const S = itemSoort(i.soort).icoon
      r.push({ sleutel: 'i' + i.id, groep: 'Inventaris', titel: i.naam, sub: [itemSoort(i.soort).label, i.code ?? i.serienummer].filter(Boolean).join(' · '), icoon: <S size={15} />, ga: ga(`/i/${i.id}`) })
    }
    for (const s of uit.systemen) {
      const S = systeemSoort(s.soort).icoon
      r.push({ sleutel: 's' + s.id, groep: 'Toegang', titel: s.naam, sub: systeemSoort(s.soort).label, icoon: <S size={15} />, ga: ga(`/s/${s.id}`) })
    }
    for (const l of uit.links) {
      r.push({ sleutel: 'l' + l.id, groep: 'Links', titel: l.titel, sub: kaalAdres(l.url), icoon: <Link2 size={15} />, ga: () => { sluit(); window.open(l.url, '_blank', 'noopener,noreferrer') } })
    }
    for (const m of uit.mensen) {
      r.push({ sleutel: 'm' + m.id, groep: 'Mensen', titel: m.naam, sub: m.functie ?? undefined, icoon: <User size={15} />, ga: ga(`/m/${m.id}`) })
    }
    return r
  }, [uit])

  useEffect(() => { setGekozen(0) }, [regels])

  function toets(e: React.KeyboardEvent) {
    if (e.key === 'Escape') sluit()
    if (e.key === 'ArrowDown') { e.preventDefault(); setGekozen((g) => Math.min(g + 1, regels.length - 1)) }
    if (e.key === 'ArrowUp') { e.preventDefault(); setGekozen((g) => Math.max(g - 1, 0)) }
    if (e.key === 'Enter') regels[gekozen]?.ga()
  }

  let vorige = ''
  return createPortal(
    <div className="scherm zoek-scherm" onMouseDown={(e) => { if (e.target === e.currentTarget) sluit() }}>
      <div className="zoek" role="dialog" aria-modal aria-label="Zoeken">
        <div className="zoek-veld">
          <Search size={18} />
          <input ref={veld} value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={toets}
            placeholder="Zoek een ticket, code, apparaat, kenteken, map of persoon…" />
        </div>
        <div className="zoek-lijst">
          {bezig && !uit && <Laden />}
          {q.trim().length < 2 && (
            <p className="menu-leeg">
              Typ minstens twee letters. Je kunt ook zoeken op een ticketnummer (42), een serienummer, een kenteken of een IMEI.
            </p>
          )}
          {uit && !regels.length && <p className="menu-leeg">Niets gevonden voor “{q}”.</p>}
          {regels.map((r, n) => {
            const kop = r.groep !== vorige ? r.groep : null
            vorige = r.groep
            return (
              <div key={r.sleutel}>
                {kop && <div className="zoek-groep">{kop}</div>}
                <button type="button" className={`zoek-regel ${n === gekozen ? 'gekozen' : ''}`}
                  onMouseEnter={() => setGekozen(n)} onClick={r.ga}>
                  <span className="zoek-icoon">{r.icoon}</span>
                  <span className="zoek-titel">{r.titel}</span>
                  {r.sub && <span className="zoek-sub">{r.sub}</span>}
                </button>
              </div>
            )
          })}
        </div>
        <div className="zoek-voet">
          <span><kbd>↑</kbd><kbd>↓</kbd> kiezen</span>
          <span><kbd>Enter</kbd> openen</span>
          <span><kbd>Esc</kbd> sluiten</span>
          <span><FolderLock size={11} /> Alleen wat jij mag zien</span>
        </div>
      </div>
    </div>,
    document.body,
  )
}
